// analyticsApi.js - API utilities for Analytics operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log('Successfully retrieved valid ID token for analytics API');
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.body ? errorData.body.error : errorData.error || 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_analytics',
        `Analytics API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[analyticsApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log(`Successfully received response from ${endpoint}:`, data);
      
      // Log API event for successful request
      loggingService.logInfo(
        'api_success_analytics',
        `Successfully completed ${method} request to ${endpoint}`,
        {
          endpoint,
          method,
        }
      ).catch(logError => {
        console.warn('[analyticsApi] Failed to log success event:', logError);
      });
      
      return data;
    } else {
      console.warn(
        `API response for ${endpoint} was not JSON. Content-Type: ${contentType}`
      );
      
      // Log API event for non-JSON response
      loggingService.logInfo(
        'api_success_analytics',
        `Successfully completed ${method} request to ${endpoint} (non-JSON response)`,
        {
          endpoint,
          method,
          contentType,
        }
      ).catch(logError => {
        console.warn('[analyticsApi] Failed to log success event:', logError);
      });
      
      return {};
    }
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_analytics',
      `Analytics API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[analyticsApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * Validates date format (YYYY-MM-DD)
 * @param {string} date - Date string to validate
 * @returns {boolean} - True if valid, false otherwise
 */
const validateDateFormat = date => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateRegex.test(date);
};

/**
 * Builds query string from parameters object
 * @param {Object} params - Object with query parameters
 * @returns {string} - Query string
 */
const buildQueryString = params => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined) {
      queryParams.append(key, params[key]);
    }
  });
  return queryParams.toString();
};

/**
 * API utility functions for Analytics.
 */
export const analyticsApi = {
  /**
   * Fetches daily attendance statistics.
   * GET /attendance/daily-stats
   * 
   * @param {Object} options - Query parameters
   * @param {string} [options.date] - Single date in YYYY-MM-DD format. Mutually exclusive with start_date/end_date.
   * @param {string} [options.start_date] - Start date of range in YYYY-MM-DD format. Required if date is not provided.
   * @param {string} [options.end_date] - End date of range in YYYY-MM-DD format. Defaults to start_date if not provided.
   * @param {string} [options.site_id] - Filter results to a specific site.
   * @param {boolean} [options.include_site_breakdown] - If true, includes per-site breakdown in response. Default: false.
   * @returns {Promise<Object>} - Response object containing attendance statistics
   * @throws {Error} - Throws an error if the API request fails or parameters are invalid
   * 
   * @example
   * // Single date
   * analyticsApi.getDailyAttendanceStats({ date: '2025-01-15' })
   * 
   * @example
   * // Date range
   * analyticsApi.getDailyAttendanceStats({ start_date: '2025-01-01', end_date: '2025-01-31' })
   * 
   * @example
   * // With site filter
   * analyticsApi.getDailyAttendanceStats({ 
   *   start_date: '2025-01-01', 
   *   end_date: '2025-01-07', 
   *   site_id: 'site_12345' 
   * })
   * 
   * @example
   * // With site breakdown
   * analyticsApi.getDailyAttendanceStats({ 
   *   date: '2025-01-15', 
   *   include_site_breakdown: true 
   * })
   */
  getDailyAttendanceStats: async ({
    date = null,
    start_date = null,
    end_date = null,
    site_id = null,
    include_site_breakdown = false,
  } = {}) => {
    try {
      // Validate that either date OR start_date is provided
      if (!date && !start_date) {
        throw new Error(
          "Must provide either 'date' or 'start_date' parameter"
        );
      }

      // Validate that date and start_date are not both provided
      if (date && start_date) {
        throw new Error(
          "Cannot use 'date' with 'start_date' or 'end_date'. Use either single 'date' or 'start_date'/'end_date' range."
        );
      }

      // Validate date format if provided
      if (date && !validateDateFormat(date)) {
        throw new Error(
          `Invalid date format: ${date}. Expected YYYY-MM-DD format.`
        );
      }

      // Validate start_date format if provided
      if (start_date && !validateDateFormat(start_date)) {
        throw new Error(
          `Invalid start_date format: ${start_date}. Expected YYYY-MM-DD format.`
        );
      }

      // Validate end_date format if provided
      if (end_date && !validateDateFormat(end_date)) {
        throw new Error(
          `Invalid end_date format: ${end_date}. Expected YYYY-MM-DD format.`
        );
      }

      // Validate date range if both start_date and end_date are provided
      if (start_date && end_date) {
        const start = new Date(start_date);
        const end = new Date(end_date);
        if (end < start) {
          throw new Error('end_date must be >= start_date');
        }
      }

      // Build query parameters
      const queryParams = {};

      if (date) {
        queryParams.date = date;
      } else {
        queryParams.start_date = start_date;
        if (end_date) {
          queryParams.end_date = end_date;
        }
      }

      if (site_id) {
        queryParams.site_id = site_id;
      }

      if (include_site_breakdown) {
        queryParams.include_site_breakdown = include_site_breakdown;
      }

      const queryString = buildQueryString(queryParams);
      const endpoint = `/attendance/daily-stats${queryString ? `?${queryString}` : ''}`;

      console.log('Analytics API: Fetching daily attendance stats with params:', queryParams);

      const response = await makeApiRequest(endpoint);

      console.log('Analytics API: Daily attendance stats response:', response);

      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_analytics',
        `Successfully fetched daily attendance stats`,
        {
          endpoint: '/attendance/daily-stats',
          method: 'GET',
          ...queryParams,
        }
      ).catch(logError => {
        console.warn('[analyticsApi] Failed to log success event:', logError);
      });

      return response;
    } catch (error) {
      console.error('Error fetching daily attendance stats:', error);
      throw error;
    }
  },

  /**
   * Fetches daily driver ride statistics.
   * GET /driver/rides/daily-stats
   * 
   * @param {Object} options - Query parameters
   * @param {string} [options.date] - Single date in YYYY-MM-DD format. Mutually exclusive with start_date/end_date.
   * @param {string} [options.start_date] - Start date of range in YYYY-MM-DD format. Required if date is not provided.
   * @param {string} [options.end_date] - End date of range in YYYY-MM-DD format. Defaults to start_date if not provided.
   * @returns {Promise<Object>} - Response object containing driver ride statistics
   * @throws {Error} - Throws an error if the API request fails or parameters are invalid
   * 
   * @example
   * // Single date
   * analyticsApi.getDailyDriverRideStats({ date: '2025-01-15' })
   * 
   * @example
   * // Date range
   * analyticsApi.getDailyDriverRideStats({ start_date: '2025-01-01', end_date: '2025-01-31' })
   * 
   * @example
   * // Start date only (defaults to single day)
   * analyticsApi.getDailyDriverRideStats({ start_date: '2025-01-15' })
   */
  getDailyDriverRideStats: async ({
    date = null,
    start_date = null,
    end_date = null,
  } = {}) => {
    try {
      // Validate that either date OR start_date is provided
      if (!date && !start_date) {
        throw new Error(
          "Must provide either 'date' or 'start_date' parameter"
        );
      }

      // Validate that date and start_date are not both provided
      if (date && start_date) {
        throw new Error(
          "Cannot use 'date' with 'start_date' or 'end_date'. Use either single 'date' or 'start_date'/'end_date' range."
        );
      }

      // Validate date format if provided
      if (date && !validateDateFormat(date)) {
        throw new Error(
          `Invalid date format: ${date}. Expected YYYY-MM-DD format.`
        );
      }

      // Validate start_date format if provided
      if (start_date && !validateDateFormat(start_date)) {
        throw new Error(
          `Invalid start_date format: ${start_date}. Expected YYYY-MM-DD format.`
        );
      }

      // Validate end_date format if provided
      if (end_date && !validateDateFormat(end_date)) {
        throw new Error(
          `Invalid end_date format: ${end_date}. Expected YYYY-MM-DD format.`
        );
      }

      // Validate date range if both start_date and end_date are provided
      if (start_date && end_date) {
        const start = new Date(start_date);
        const end = new Date(end_date);
        if (end < start) {
          throw new Error('end_date must be >= start_date');
        }
      }

      // Build query parameters
      const queryParams = {};

      if (date) {
        queryParams.date = date;
      } else {
        queryParams.start_date = start_date;
        if (end_date) {
          queryParams.end_date = end_date;
        }
      }

      const queryString = buildQueryString(queryParams);
      const endpoint = `/driver/rides/daily-stats${queryString ? `?${queryString}` : ''}`;

      console.log('Analytics API: Fetching daily driver ride stats with params:', queryParams);

      const response = await makeApiRequest(endpoint);

      console.log('Analytics API: Daily driver ride stats response:', response);

      // Log API event for successful fetch
      loggingService.logInfo(
        'api_success_analytics',
        `Successfully fetched daily driver ride stats`,
        {
          endpoint: '/driver/rides/daily-stats',
          method: 'GET',
          ...queryParams,
        }
      ).catch(logError => {
        console.warn('[analyticsApi] Failed to log success event:', logError);
      });

      return response;
    } catch (error) {
      console.error('Error fetching daily driver ride stats:', error);
      throw error;
    }
  },
};

